import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { ChevronDown } from 'lucide-react';
import WaitlistHeader from './WaitlistHeader';
import './ManifestoPage.css';

/* ────────────────────────────────────────────────────
   ManifestoPage. "The End of Generic AI"
   Cinematic, scroll-driven editorial experience.
   8 movements + founder signature + CTA.
   ──────────────────────────────────────────────────── */

const ManifestoPage: React.FC = () => {
    const { t } = useTranslation('marketing');
    const [progress, setProgress] = useState(0);
    const sectionRefs = useRef<(HTMLElement | null)[]>([]);

    /* ── Scroll progress ── */
    const handleScroll = useCallback(() => {
        const scrollTop = window.scrollY;
        const docHeight = document.documentElement.scrollHeight - window.innerHeight;
        setProgress(docHeight > 0 ? Math.min((scrollTop / docHeight) * 100, 100) : 0);
    }, []);

    useEffect(() => {
        window.addEventListener('scroll', handleScroll, { passive: true });
        handleScroll();
        return () => window.removeEventListener('scroll', handleScroll);
    }, [handleScroll]);

    /* ── IntersectionObserver-based reveals ── */
    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) =>
                entries.forEach((e) => {
                    if (e.isIntersecting) e.target.classList.add('visible');
                }),
            { threshold: 0.12 }
        );
        document.querySelectorAll('.mn-reveal').forEach((el) => observer.observe(el));
        return () => observer.disconnect();
    }, []);

    /* ── Helper: word-by-word animation on hero title ── */
    const heroTitle = t('manifesto.hero_title', 'The End of Generic AI');
    const heroWords = heroTitle.split(' ');

    return (
        <div
            className="manifesto-root min-h-screen flex flex-col overflow-x-hidden relative"
            style={{ background: 'var(--bg-primary)' }}
        >
            {/* ── Atmospheric background ── */}
            <div className="mn-fixed-bg">
                <div className="mn-bg-gradient" />
                <div className="mn-bg-glow-1" />
                <div className="mn-bg-glow-2" />
                <div className="mn-bg-noise" />
            </div>

            <div className="mn-progress" style={{ width: `${progress}%` }} />

            <WaitlistHeader />

            {/* ════════════════ HERO ════════════════ */}
            <section className="mn-hero" id="manifesto-hero">
                <motion.span
                    className="mn-hero-eyebrow"
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.2 }}
                >
                    {t('manifesto.hero_eyebrow', 'A HumanReplica® Manifesto')}
                </motion.span>

                <h1>
                    {heroWords.map((word, i) => (
                        <motion.span
                            key={i}
                            initial={{ opacity: 0, y: 30, filter: 'blur(8px)' }}
                            animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
                            transition={{
                                duration: 0.6,
                                delay: 0.4 + i * 0.1,
                                ease: [0.16, 1, 0.3, 1],
                            }}
                            style={{ display: 'inline-block', marginRight: i < heroWords.length - 1 ? '0.3em' : 0 }}
                        >
                            {word}
                        </motion.span>
                    ))}
                </h1>

                <motion.div
                    className="mn-hero-scroll-indicator"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 1, delay: 1.5 }}
                >
                    <span>{t('manifesto.scroll_cue', 'Read')}</span>
                    <ChevronDown size={16} />
                </motion.div>
            </section>

            {/* ════════════════ 01. THE OPENING ════════════════ */}
            <section
                ref={(el) => { sectionRefs.current[0] = el; }}
                className="mn-section mn-reveal"
                id="manifesto-opening"
            >
                <span className="mn-section-num">{t('manifesto.s1_num', '01. The Opening')}</span>

                <div className="mn-lines">
                    <motion.span
                        initial={{ opacity: 0, x: -20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5, delay: 0.1 }}
                    >
                        {t('manifesto.s1_line1', 'We are entering a world where everything starts to look the same.')}
                    </motion.span>
                </div>

                <div className="mn-lines" style={{ paddingLeft: '1.5rem' }}>
                    {(['s1_line2', 's1_line3', 's1_line4', 's1_line5'] as const).map((key, i) => (
                        <motion.span
                            key={key}
                            initial={{ opacity: 0, x: -12 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.4, delay: 0.2 + i * 0.08 }}
                            style={{ fontSize: '1rem', color: 'var(--mn-text-muted)' }}
                        >
                            {t(`manifesto.${key}`)}
                        </motion.span>
                    ))}
                </div>

                <p style={{ marginTop: '2rem' }}>
                    <strong>{t('manifesto.s1_bold', 'This is not intelligence.')}</strong><br />
                    {t('manifesto.s1_compression', 'This is compression.')}
                </p>

                <p>{t('manifesto.s1_body')}</p>
            </section>

            {/* ════════════════ 02. THE REJECTION ════════════════ */}
            <section
                ref={(el) => { sectionRefs.current[1] = el; }}
                className="mn-section mn-reveal"
                id="manifesto-rejection"
            >
                <span className="mn-section-num">{t('manifesto.s2_num', '02. The Rejection')}</span>

                <h2>{t('manifesto.s2_title', 'We Reject Generic Intelligence')}</h2>

                <p>{t('manifesto.s2_intro')}</p>

                <ul className="mn-reject-list">
                    {(['s2_reject1', 's2_reject2', 's2_reject3', 's2_reject4'] as const).map((key, i) => (
                        <motion.li
                            key={key}
                            className="mn-reject-item"
                            initial={{ opacity: 0, x: -16 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.4, delay: i * 0.1 }}
                        >
                            <span>{t(`manifesto.${key}`)}</span>
                        </motion.li>
                    ))}
                </ul>

                <div className="mn-pullquote">
                    <p>{t('manifesto.s2_quote', 'Because intelligence without identity is not intelligence. It is imitation.')}</p>
                </div>
            </section>

            {/* ════════════════ 03. THE BELIEF ════════════════ */}
            <section
                ref={(el) => { sectionRefs.current[2] = el; }}
                className="mn-section mn-reveal"
                id="manifesto-belief"
            >
                <span className="mn-section-num">{t('manifesto.s3_num', '03. The Belief')}</span>

                <h2>{t('manifesto.s3_title')}</h2>

                <div className="mn-belief-block">
                    {(['s3_belief1', 's3_belief2', 's3_belief3', 's3_belief4'] as const).filter((key) => t(`manifesto.${key}`)).map((key, i) => (
                        <motion.p
                            key={key}
                            initial={{ opacity: 0, y: 12 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.5, delay: i * 0.12 }}
                        >
                            {t(`manifesto.${key}`)}
                        </motion.p>
                    ))}
                </div>

                <p><strong>{t('manifesto.s3_trained_on_you')}</strong></p>

                <div className="mn-lines" style={{ paddingLeft: '1.5rem' }}>
                    {(['s3_your1', 's3_your2', 's3_your3', 's3_your4'] as const).map((key, i) => (
                        <motion.span
                            key={key}
                            initial={{ opacity: 0, x: -10 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.4, delay: 0.1 + i * 0.08 }}
                        >
                            {t(`manifesto.${key}`)}
                        </motion.span>
                    ))}
                </div>

                <div className="mn-pullquote" style={{ marginTop: '2.5rem' }}>
                    <p>{t('manifesto.s3_quote', 'It does not generate content. It expresses you.')}</p>
                </div>
            </section>

            {/* ════════════════ 04. THE SHIFT ════════════════ */}
            <section
                ref={(el) => { sectionRefs.current[3] = el; }}
                className="mn-section mn-reveal"
                id="manifesto-shift"
            >
                <span className="mn-section-num">{t('manifesto.s4_num', '04. From Tool to Extension')}</span>

                <h2>{t('manifesto.s4_title')}</h2>

                <p>{t('manifesto.s4_intro')}</p>

                <div className="mn-lines" style={{ margin: '2rem 0', gap: '1rem' }}>
                    {(['s4_not1', 's4_not2', 's4_not3'] as const).map((key, i) => (
                        <motion.span
                            key={key}
                            initial={{ opacity: 0, y: 10 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.5, delay: i * 0.15 }}
                            style={{ fontSize: '1.05rem', color: 'var(--mn-text)' }}
                        >
                            {t(`manifesto.${key}`)}
                        </motion.span>
                    ))}
                </div>
            </section>

            {/* ════════════════ 05. THE SIGNATURE ════════════════ */}
            <section
                ref={(el) => { sectionRefs.current[4] = el; }}
                className="mn-section mn-reveal"
                id="manifesto-signature-section"
            >
                <span className="mn-section-num">{t('manifesto.s5_num', '05. The Return of Signature')}</span>

                <h2>{t('manifesto.s5_title')}</h2>

                <p>{t('manifesto.s5_body1')}</p>

                <p><strong>{t('manifesto.s5_bold', 'But identity.')}</strong></p>

                <p>{t('manifesto.s5_body2')}</p>

                <div className="mn-lines" style={{ paddingLeft: '1rem', margin: '2rem 0' }}>
                    {(['s5_trace1', 's5_trace2', 's5_trace3'] as const).map((key, i) => (
                        <motion.span
                            key={key}
                            initial={{ opacity: 0 }}
                            whileInView={{ opacity: 1 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.5, delay: 0.15 + i * 0.12 }}
                            style={{ color: 'var(--mn-accent)', fontSize: '1rem' }}
                        >
                            {t(`manifesto.${key}`)}
                        </motion.span>
                    ))}
                </div>
            </section>

            {/* ════════════════ 06. A NEW INTERNET ════════════════ */}
            <section
                ref={(el) => { sectionRefs.current[5] = el; }}
                className="mn-section mn-reveal"
                id="manifesto-network"
            >
                <span className="mn-section-num">{t('manifesto.s6_num', '06. A New Internet')}</span>

                <h2>{t('manifesto.s6_title')}</h2>

                <p>{t('manifesto.s6_dead_internet')}</p>

                <div className="mn-pullquote">
                    <p>{t('manifesto.s6_quote')}</p>
                </div>

                <p>{t('manifesto.s6_living')}</p>
            </section>

            {/* ════════════════ 07. HUMAN IN COMMAND ════════════════ */}
            <section
                ref={(el) => { sectionRefs.current[6] = el; }}
                className="mn-section mn-reveal"
                id="manifesto-command"
            >
                <span className="mn-section-num">{t('manifesto.s7_num', '07. Human in Command')}</span>

                <h2>{t('manifesto.s7_title', 'Human in Command. Always.')}</h2>

                <div className="mn-lines" style={{ gap: '0.35rem' }}>
                    {(['s7_line1', 's7_line2', 's7_line3'] as const).map((key, i) => (
                        <motion.span
                            key={key}
                            initial={{ opacity: 0, y: 8 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.4, delay: i * 0.08 }}
                            style={{ color: 'var(--mn-text-sec)' }}
                        >
                            {t(`manifesto.${key}`)}
                        </motion.span>
                    ))}
                </div>

                <div className="mn-lines" style={{ gap: '0.35rem', marginTop: '1.5rem' }}>
                    {(['s7_gov1', 's7_gov2', 's7_gov3', 's7_gov4'] as const).map((key, i) => (
                        <motion.span
                            key={key}
                            initial={{ opacity: 0, x: -8 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.4, delay: 0.3 + i * 0.08 }}
                            style={{ color: 'var(--mn-accent)', fontSize: '1rem' }}
                        >
                            {t(`manifesto.${key}`)}
                        </motion.span>
                    ))}
                </div>

                <div className="mn-pullquote" style={{ marginTop: '2.5rem' }}>
                    <p>{t('manifesto.s7_quote', 'Because the future of AI is not autonomy. It is alignment.')}</p>
                </div>
            </section>

            {/* ════════════════ 08. FOR COMPANIES ════════════════ */}
            <section
                ref={(el) => { sectionRefs.current[7] = el; }}
                className="mn-section mn-reveal"
                id="manifesto-companies"
            >
                <span className="mn-section-num">{t('manifesto.s8_num', '08. For Companies')}</span>

                <h2>{t('manifesto.s8_title', 'Companies Will Not Survive As They Are')}</h2>

                <p>{t('manifesto.s8_intro')}</p>
                <p><strong style={{ color: 'var(--mn-accent)' }}>{t('manifesto.s8_intro2')}</strong></p>

                <p style={{ marginTop: '2rem' }}>{t('manifesto.s8_generic')}</p>

                <div className="mn-lines" style={{ paddingLeft: '1.5rem' }}>
                    {(['s8_same1', 's8_same2', 's8_same3'] as const).map((key, i) => (
                        <motion.span
                            key={key}
                            initial={{ opacity: 0, x: -12 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.4, delay: 0.1 + i * 0.08 }}
                            style={{ fontSize: '1rem', color: 'var(--mn-text-muted)' }}
                        >
                            {t(`manifesto.${key}`)}
                        </motion.span>
                    ))}
                </div>

                <p style={{ marginTop: '2rem' }}><strong>{t('manifesto.s8_augment')}</strong></p>

                {/* Multiplier grid */}
                <div className="mn-shift-grid" style={{ maxWidth: '480px', margin: '2rem auto' }}>
                    {(['mult1', 'mult2', 'mult3'] as const).map((key, i) => (
                        <React.Fragment key={key}>
                            <motion.span
                                className="mn-shift-from"
                                initial={{ opacity: 0 }}
                                whileInView={{ opacity: 1 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.4, delay: 0.1 + i * 0.15 }}
                                style={{ fontWeight: 600 }}
                            >
                                {t(`manifesto.s8_${key}_label`)}
                            </motion.span>
                            <span className="mn-shift-arrow">→</span>
                            <motion.span
                                className="mn-shift-to"
                                initial={{ opacity: 0, x: 10 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.5, delay: 0.2 + i * 0.15 }}
                            >
                                {t(`manifesto.s8_${key}_value`)}
                            </motion.span>
                        </React.Fragment>
                    ))}
                </div>

                <h3 style={{ marginTop: '3rem', color: 'var(--mn-heading)', fontSize: '1.4rem' }}>
                    {t('manifesto.s8_choice_title', 'The Choice Is Simple')}
                </h3>

                <div className="mn-lines" style={{ gap: '0.5rem', marginTop: '1rem' }}>
                    {(['s8_choice1', 's8_choice2'] as const).filter((key) => t(`manifesto.${key}`)).map((key, i) => (
                        <motion.span
                            key={key}
                            initial={{ opacity: 0, y: 8 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.4, delay: i * 0.1 }}
                            style={{ color: 'var(--mn-text-sec)' }}
                        >
                            {t(`manifesto.${key}`)}
                        </motion.span>
                    ))}
                </div>

                <p style={{ marginTop: '1.5rem' }}>{t('manifesto.s8_choice3')}</p>

                <div className="mn-pullquote" style={{ marginTop: '1rem' }}>
                    <p style={{ fontSize: '1.3rem', fontWeight: 700 }}>
                        {t('manifesto.s8_choice_bold', 'Turn one employee into the equivalent of a team.')}
                    </p>
                </div>

                <div style={{ marginTop: '3rem', textAlign: 'center' }}>
                    <motion.p
                        initial={{ opacity: 0, y: 12 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6, delay: 0.2 }}
                        style={{
                            fontFamily: 'var(--mn-serif)',
                            fontSize: 'clamp(1.3rem, 2.5vw, 1.8rem)',
                            color: 'var(--mn-accent)',
                            fontWeight: 600,
                            marginBottom: '0.5rem',
                        }}
                    >
                        {t('manifesto.s8_hr_title', 'HumanReplica for Companies')}
                    </motion.p>
                    <motion.p
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5, delay: 0.4 }}
                        style={{ color: 'var(--mn-text-sec)' }}
                    >
                        {t('manifesto.s8_hr_line1')}
                    </motion.p>
                    <motion.p
                        initial={{ opacity: 0, scale: 0.95 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6, delay: 0.55 }}
                        style={{
                            color: 'var(--mn-heading)',
                            fontWeight: 700,
                            fontSize: '1.2rem',
                            marginTop: '0.25rem',
                        }}
                    >
                        {t('manifesto.s8_hr_line2')}
                    </motion.p>
                </div>
            </section>

            {/* ════════════════ CLOSING. This Is Not a Product ════════════════ */}
            <div className="mn-closing mn-reveal" id="manifesto-closing">
                <h2><em>{t('manifesto.closing_title', 'This Is Not a Product')}</em></h2>
                <p>{t('manifesto.closing_body', 'This is a shift.')}</p>

                <div className="mn-shift-grid" style={{ maxWidth: '440px', margin: '2rem auto 3rem' }}>
                    {(['shift1', 'shift2', 'shift3'] as const).map((key, i) => (
                        <React.Fragment key={key}>
                            <motion.span
                                className="mn-shift-from"
                                initial={{ opacity: 0 }}
                                whileInView={{ opacity: 1 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.4, delay: 0.1 + i * 0.15 }}
                            >
                                {t(`manifesto.${key}_from`)}
                            </motion.span>
                            <span className="mn-shift-arrow">→</span>
                            <motion.span
                                className="mn-shift-to"
                                initial={{ opacity: 0, x: 10 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.5, delay: 0.2 + i * 0.15 }}
                            >
                                {t(`manifesto.${key}_to`)}
                            </motion.span>
                        </React.Fragment>
                    ))}
                </div>

                <p style={{ textAlign: 'center', fontWeight: 600, color: 'var(--mn-heading)', fontSize: '1.1rem' }}>
                    {t('manifesto.closing_statement', 'We are not building better AI tools.')}
                </p>
                <p style={{ textAlign: 'center', color: 'var(--mn-text-sec)' }}>
                    {t('manifesto.closing_statement2', 'We are building the infrastructure for human presence in the age of AI.')}
                </p>

                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: 0.3 }}
                    style={{ marginTop: '2rem', textAlign: 'center' }}
                >
                    <p style={{
                        fontFamily: 'var(--mn-serif)',
                        fontSize: '1.1rem',
                        fontStyle: 'italic',
                        color: 'var(--mn-accent)',
                        textAlign: 'center',
                    }}>
                        {t('manifesto.tagline', 'Not artificial intelligence. Human intelligence. extended.')}
                    </p>

                    {/* ── Team signature — centered, elegant ── */}
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '1rem',
                        marginTop: '2.5rem',
                    }}>
                        <span style={{
                            width: '32px',
                            height: '2px',
                            background: 'var(--mn-accent)',
                            borderRadius: '1px',
                            flexShrink: 0,
                        }} />
                        <span style={{
                            fontFamily: 'var(--mn-mono)',
                            fontSize: '0.72rem',
                            letterSpacing: '0.18em',
                            textTransform: 'uppercase' as const,
                            color: 'var(--mn-text-sec)',
                        }}>
                            {t('manifesto.founder_title', 'The Team')}
                        </span>
                        <span style={{
                            width: '32px',
                            height: '2px',
                            background: 'var(--mn-accent)',
                            borderRadius: '1px',
                            flexShrink: 0,
                        }} />
                    </div>
                </motion.div>

                <div style={{ marginTop: '3rem' }}>
                    <a href="/" className="mn-cta-btn">
                        {t('manifesto.cta', 'Request Early Access')} →
                    </a>
                </div>
            </div>

            {/* ════════════════ FOOTER ════════════════ */}
            <footer className="mn-footer">
                <div className="mn-footer-left">
                    © 2026 HumanReplica.ai®. {t('manifesto.footer_tagline', 'The End of Generic AI')}
                </div>
                <div className="mn-footer-social">
                    <a href="https://x.com/humanreplica_ai" target="_blank" rel="noopener noreferrer" aria-label="X">
                        <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                    </a>
                    <a href="https://linkedin.com/company/humanreplica-ai" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
                        <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                    </a>
                </div>
                <div className="mn-footer-right">
                    <a href="/whitepaper">{t('manifesto.footer_whitepaper', 'Whitepaper')}</a>
                    <a href="/privacy">{t('manifesto.footer_privacy', 'Privacy')}</a>
                </div>
            </footer>
        </div>
    );
};

export default ManifestoPage;
